import { Bolt, CreditCard, Receipt } from "@mui/icons-material";
import TestDias from "./TestDias";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Divider,
  Paper,
  Skeleton,
  Tab,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import PaymentForm from "../../creditCard/features/paymentForm/PaymentForm";
import FileDownloadButton from "../../applications/shared/components/FileDownloadButton";
import InfoMessage from "src/shared/components/InfoMessage";
import { useAuth } from "../context/useAuth";
import DepositForm from "../../deposit/features/DepositForm/DepositForm";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";
import { enqueueSnackbar } from "notistack";
import ContentCopy from "@mui/icons-material/ContentCopy";
import useDeclarePaymentCompletedAndForwardToChamber from "src/violationCase/data-access/useDeclarePaymentCompletedAndForwardToChamber";
import PaymentCheckbox from "src/violationCase/components/PaymentCheckbox";

export type TPaymentTabsProps = {
  appId?: string | null;
  mode:
    | "subscription"
    | "app"
    | "cert"
    | "namePurchase"
    | "namePurchaseApplication"
    | "violationCase";
  ri3?: string;
  year?: string;
  amount: string;
  subscriptionStart?: string;
  isGGEFromProps?: boolean;
  relatedId?: string;
  relatedType?: string;
  finePaymentId?: number;
};
function PaymentTabs(props: TPaymentTabsProps) {
  const {
    appId,
    mode,
    ri3,
    year,
    amount,
    subscriptionStart,
    isGGEFromProps,
    relatedType,
    relatedId,
    finePaymentId,
  } = props;
  const { t } = useTranslation();
  const { isGGE } = useAuth();

  const depositMode = isGGEFromProps ?? isGGE;

  const namePurchaseMode =
    mode === "namePurchase" || mode === "namePurchaseApplication";
  const determineActiveTab = () => {
    if (!depositMode && !namePurchaseMode) {
      return "1";
    }
    return !depositMode || mode === "namePurchase" ? "2" : "4";
  };
  const [activeTab, setActiveTab] = useState(determineActiveTab);
  const declarePaymentMutation =
    useDeclarePaymentCompletedAndForwardToChamber(appId);
  const [paymentAccepted, setPaymentAccepted] = useState(false);
  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        enqueueSnackbar(t("Επιτυχής αντιγραφή!"), {
          variant: "success",
        });
      })
      .catch(() => {
        enqueueSnackbar(t("Η αντιγραφή απέτυχε"), {
          variant: "error",
        });
      });
  };
  return (
    <Paper sx={{ width: "100%", p: "1em", mx: "auto", maxWidth: "80vw" }}>
      <h3>{t("Πληρωμή Αίτησης")}</h3>
      {(import.meta.env.DEV || import.meta.env.MODE === "azureDev") &&
        (!depositMode || namePurchaseMode) && (
          <InfoMessage
            variant={MessageVariant.Info}
            boldWord="Τρόποι πληρωμής (DEV)"
            message={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                Παρακάτω βλέπετε τους τρόπους πληρωμής που μπορείτε να
                χρησιμοποιήσετε για να πληρώσετε μία αίτηση σε dev περιβάλλον
                <ol>
                  <li>
                    FRICTIONLESS :
                    <ul>
                      Τύπος Κάρτας: <strong>Visa</strong>, Αριθμός κάρτας:{" "}
                      <strong>4908455555555557</strong>, Μήνας:{" "}
                      <strong>10</strong>, Έτος:{" "}
                      <strong>οποιοδήποτε μελλοντικό έτος</strong>,
                    </ul>
                  </li>

                  <li>
                    CHALLENGE :
                    <ul>
                      Τύπος Κάρτας: <strong>Visa</strong>, Αριθμός κάρτας:{" "}
                      <strong>4908456666666663</strong>, Μήνας:{" "}
                      <strong>05</strong>, Έτος:{" "}
                      <strong>οποιοδήποτε μελλοντικό έτος</strong>,
                    </ul>
                    <ul>
                      Τύπος Κάρτας: <strong>MASTERCARD</strong>, Αριθμός κάρτας:{" "}
                      <strong>5194992222222229</strong>, Μήνας:{" "}
                      <strong>01</strong>, Έτος:{" "}
                      <strong>οποιοδήποτε μελλοντικό έτος</strong>,
                    </ul>
                  </li>
                </ol>
              </Box>
            }
          />
        )}
      <TabContext value={activeTab}>
        <TabList
          onChange={(_, value) => setActiveTab(value)}
          sx={{
            "& .MuiTab-root": {
              fontSize: "1.5rem",
              "& .MuiSvgIcon-root": {
                fontSize: "1.9rem",
                marginBottom: 0.5,
              },
            },
          }}
        >
          {!depositMode && !namePurchaseMode && (
            <Tab icon={<Bolt />} label={t("ΔΙΑΣ")} value={"1"} />
          )}
          {(!depositMode || namePurchaseMode) && (
            <Tab icon={<CreditCard />} label={t("Κάρτα")} value={"2"} />
          )}

          {(import.meta.env.DEV || import.meta.env.MODE === "azureDev") &&
            !depositMode &&
            !namePurchaseMode && (
              <Tab icon={<Bolt />} label={t("Τεστ")} value={"3"} />
            )}
          {depositMode && !namePurchaseMode && (
            <Tab
              icon={<Receipt />}
              label={t("Ηλεκτρονικό Παράβολο")}
              value={"4"}
            />
          )}
        </TabList>
        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          {!depositMode && !namePurchaseMode && (
            <TabPanel value={"1"}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "1rem",
                  width: "80%",
                  mx: "auto",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  {ri3 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Tooltip title={t("Αντιγραφή Κωδικού RF")}>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            copyToClipboard(ri3.replace(/\s/g, ""))
                          }
                        >
                          {ri3?.replace(/.{5}/g, "$& ")}
                        </span>
                      </Tooltip>
                      <Button
                        endIcon={<ContentCopy />}
                        variant="outlined"
                        onClick={() => copyToClipboard(ri3.replace(/\s/g, ""))}
                      >
                        {t("Αντιγραφή Κωδικού RF")}
                      </Button>
                    </Box>
                  ) : (
                    <Skeleton variant="rectangular" width={50} />
                  )}
                </Typography>
                {!!ri3 && (
                  <>
                    <FileDownloadButton
                      variant="contained"
                      url={`${import.meta.env.VITE_APP_API_URL}/document/downloadRi3Invoice`}
                      params={[
                        { key: "ri3", value: ri3 },
                        { key: "isSepaInvoice", value: "false" },
                      ]}
                      text={t("Λήψη Εντύπου Πληρωμής ΔΙΑΣ")}
                    />
                    <FileDownloadButton
                      variant="contained"
                      url={`${import.meta.env.VITE_APP_API_URL}/document/downloadRi3Invoice`}
                      params={[
                        { key: "ri3", value: ri3 },
                        { key: "isSepaInvoice", value: "true" },
                      ]}
                      text={t(
                        "Λήψη Εντύπου Πληρωμής SEPA (Για τράπεζα του ΕΞΩΤΕΡΙΚΟΥ ΜΟΝΟ)",
                      )}
                    />
                  </>
                )}
                <Typography>
                  {t(
                    'Μπορείτε να πληρώσετε είτε σε ταμείο είτε με το web banking / phone banking της τράπεζάς σας. Συμβουλευθείτε το παραπάνω έντυπο πληρωμής για τους διαθέσιμους τρόπους πληρωμής με χρήση του 20ψήφιου κωδικού. Αναζητείστε στο web banking σας την επιλογή δικαιούχου "Κεντρική Ένωση Επιμελητηρίων Ελλάδας (ΚΕΕΕ)" (Κάτω από την ενότητα "Πληρωμές", υποενότητα "Επιμελητήρια"). Σημειώνεται ότι η ενημέρωση του ΓΕΜΗ για πληρωμές με αυτόν τον τρόπο γίνεται μέχρι και 2 εργάσιμες μέρες μετά από την πληρωμή σας, σε αντίθεση με την πληρωμή με κάρτα όπου η ενημέρωση είναι άμεση.',
                  )}
                </Typography>
              </Box>
              {mode === "violationCase" && appId && (
                <>
                  <PaymentCheckbox
                    checked={paymentAccepted}
                    onCheckedChange={setPaymentAccepted}
                  />
                  <Button
                    variant="contained"
                    color={"info"}
                    disabled={!paymentAccepted}
                    onClick={() => declarePaymentMutation.mutate(appId)}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "1rem",
                      width: "100%",
                      mt: "1rem",
                    }}
                  >
                    {t(
                      "Δήλωση πληρωμής και προώθηση στην αρμόδια Υ.Γ.Ε.ΜΗ. για έλεγχο",
                    )}
                  </Button>
                </>
              )}
            </TabPanel>
          )}
          {(!depositMode || namePurchaseMode) && (
            <TabPanel sx={{ width: "100%" }} value={"2"}>
              <PaymentForm
                appId={appId}
                mode={mode}
                year={year}
                relatedId={relatedId}
                relatedType={relatedType}
                finePaymentId={finePaymentId}
              />
            </TabPanel>
          )}
          {(import.meta.env.DEV || import.meta.env.MODE === "azureDev") &&
            !depositMode &&
            !namePurchaseMode && (
              <TabPanel value="3">
                <TestDias ri3={ri3} amount={amount} />
              </TabPanel>
            )}
          {depositMode && !namePurchaseMode && (
            <TabPanel value="4">
              <DepositForm
                mode={mode}
                appId={appId}
                subscriptionStart={subscriptionStart}
                finePaymentId={finePaymentId}
              />
            </TabPanel>
          )}
        </Box>
      </TabContext>
    </Paper>
  );
}

export default PaymentTabs;
